import React from "react";
import { theme } from "../styles/theme";

// B244 (AI_110) — Renderiza o markdown simples das análises de IA (títulos,
// negrito, listas, citações, linhas horizontais) SEM dependência externa
// (react-markdown é ESM e quebra o jest do CRA). É robusto a "parede de texto":
// o modelo às vezes devolve tudo corrido, com os marcadores (##, ---, ###)
// inline — aqui normalizamos inserindo quebras antes deles antes de renderizar.

// Renderiza **negrito** dentro de um trecho de texto.
function renderInline(texto: string, keyBase: string): React.ReactNode[] {
  const partes = texto.split(/(\*\*[^*]+\*\*)/g);
  return partes.map((p, i) =>
    /^\*\*[^*]+\*\*$/.test(p) ? (
      <strong key={`${keyBase}-b${i}`}>{p.replace(/\*\*/g, "")}</strong>
    ) : (
      <React.Fragment key={`${keyBase}-t${i}`}>{p}</React.Fragment>
    )
  );
}

// Insere quebras de linha antes de marcadores que podem vir "colados" no texto.
// Exportada para teste unitário (lógica pura, sem React).
export function normalizar(texto: string): string {
  const semCr = texto
    .replace(/\r/g, "")
    // "##" (ou ###) sozinho numa linha + título na linha seguinte → junta numa
    // só linha de título ("##\n\n1. PADRÃO" → "## 1. PADRÃO"), p/ virar seção.
    .replace(/(^|\n)(#{1,4})[ \t]*\n+[ \t]*/g, "$1$2 ");

  // 🐛 B339 (AI_149) — as regras abaixo passam a rodar LINHA A LINHA, pulando as
  // linhas de tabela. Motivo: a régua `---` era global e mordia a linha
  // SEPARADORA do cabeçalho ("|------|------|" contém "------"), estilhaçando a
  // tabela em "|" soltos + réguas horizontais + um "--|" órfão — exatamente o
  // que Leonardo viu na análise de imagem. Tabela bem-formada nem chega aqui
  // (separarPartes a extrai antes), mas a malformada chegava e virava lixo.
  return semCr
    .split("\n")
    .map((l) =>
      ehLinhaTabela(l) || ehSeparadorTabela(l)
        ? l
        : l
            .replace(/\s*---\s*/g, "\n\n---\n\n") // linha horizontal
            .replace(/([^\n])[ \t]+(#{1,4})[ \t]+/g, "$1\n\n$2 ") // títulos colados
            // 🐛 B382 (AI_156) — o ">" de MAIOR QUE não é citação. A regra antiga
            // (`([^\n])[ \t]+>[ \t]+`) quebrava antes de QUALQUER "> " no meio da
            // linha, e "MELD-Na > 10" virava "MELD-Na" + linha "> 10" — que o
            // renderizador então tratava como citação e apagava o ">". Resultado
            // no texto real (análise da biópsia, 01/11): "MELD-Na" seguido de
            // "10", ou seja, o LIMIAR de um critério regulatório desaparecia.
            // Agora a quebra só acontece quando o ">" abre citação de verdade:
            // depois de fim de frase. Comparação inline fica intacta.
            .replace(/([.;:!?)])[ \t]+>[ \t]+/g, "$1\n> ") // citação colada no meio
            // item numerado em texto corrido (NÃO quebrar "## 1." de uma seção)
            .replace(/([^\s#])\s+(\d{1,2}\.\s)/g, "$1\n$2")
            .replace(/\s+-\s+\*\*/g, "\n- **") // bullet "- **rótulo**" colado
    )
    .join("\n");
}

// B301/AI_137 — remove do TEXTO EXIBIDO o bloco ```ACHADOS_EXAME ... ``` (é um
// artefato INTERNO, lido pelo parser p/ preencher os campos estruturados; o médico
// não deve vê-lo) + o título da seção que o introduz ("## 7. RESUMO ESTRUTURADO") +
// cercas ``` órfãs. Exportada para teste. Robusta a variações do modelo.
export function removerBlocoTecnico(texto: string): string {
  return String(texto || "")
    .replace(/\r/g, "")
    // o bloco cercado inteiro (com as duas cercas)
    .replace(/```\s*ACHADOS_EXAME[\s\S]*?```/gi, "")
    // O título da seção "RESUMO ESTRUTURADO" (com ou sem #, com ou sem número).
    // 🐛 B339 (AI_149): o modelo escreve "## 9. 📋 RESUMO ESTRUTURADO" e o EMOJI
    // (ou qualquer enfeite entre o número e o texto) fazia a regex falhar → o
    // CONTEÚDO do bloco era removido e o TÍTULO sobrava ÓRFÃO no fim da análise
    // (o "título fora de lugar" que Leonardo viu). Agora tolera qualquer coisa
    // que não seja letra entre o "#"/número e as palavras.
    .replace(
      /(^|\n)[ \t]*#{0,4}[^\S\n]*[\d.]*[^\p{L}\n]*RESUMO ESTRUTURADO[ \t]*(?=\n|$)/giu,
      "$1"
    )
    // cercas ``` órfãs remanescentes em linha própria
    .replace(/(^|\n)[ \t]*```[a-z_]*[ \t]*(?=\n|$)/gi, "$1")
    // "---" logo antes do fim (separador que sobrou sem seção depois)
    .replace(/\n[ \t]*---[ \t]*\s*$/g, "")
    .trim();
}

// ── B305/AI_138 — SUPORTE A TABELA MARKDOWN ────────────────────────
// O modelo às vezes formata seções tabulares (ex.: "| Sítio | SUVmax | Obs |")
// como tabela markdown. O renderizador não a tratava → cada linha caía em <p> e o
// normalizar() ainda transformava o "---" da linha separadora em régua horizontal,
// espatifando a tabela (linhas "|" soltas). Aqui detectamos o bloco de tabela ANTES
// do normalizar e o renderizamos como <table>. Puro (testável).

/**
 * Tira o marcador de citação do início da linha ("> | a | b |" → "| a | b |").
 * 🐛 B339 (AI_149): o modelo às vezes embrulha a tabela inteira num blockquote.
 * Sem isto, `ehLinhaTabela` falhava (a linha começa com ">"), a tabela caía no
 * caminho de TEXTO e era estilhaçada. Só remove o ">" quando o que sobra é
 * mesmo linha de tabela — citação normal continua citação.
 */
export function tirarCitacao(l: string): string {
  const t = l.trim();
  if (!t.startsWith(">")) return l;
  const resto = t.replace(/^>[ \t]?/, "");
  return resto.trim().startsWith("|") ? resto : l;
}

/** Linha que faz parte de uma tabela markdown (começa com "|", mesmo citada). */
export function ehLinhaTabela(l: string): boolean {
  const t = tirarCitacao(l).trim();
  return t.startsWith("|") && t.length > 1;
}

/** Linha separadora do cabeçalho: só "|", "-", ":" e espaços, com ao menos um "-". */
export function ehSeparadorTabela(l: string): boolean {
  const t = tirarCitacao(l).trim();
  return /^[\s|:-]+$/.test(t) && t.includes("-") && t.includes("|");
}

/** Divide uma linha "| a | b | c |" nas células ["a","b","c"] (trim; sem as bordas). */
export function parseLinhaTabela(l: string): string[] {
  let t = tirarCitacao(l).trim();
  if (t.startsWith("|")) t = t.slice(1);
  if (t.endsWith("|")) t = t.slice(0, -1);
  return t.split("|").map((c) => c.trim());
}

/**
 * Separa o texto (já sem o bloco técnico) em partes de TEXTO e de TABELA.
 * Tabela = linha de cabeçalho "|...|" seguida (após linhas vazias) por uma linha
 * SEPARADORA "|---|". Tolera linhas vazias entre as linhas de dados.
 */
export function separarPartes(
  semTecnico: string
): Array<{ tipo: "texto"; texto: string } | { tipo: "tabela"; linhas: string[] }> {
  const linhas = semTecnico.split(/\n/);
  const partes: Array<
    { tipo: "texto"; texto: string } | { tipo: "tabela"; linhas: string[] }
  > = [];
  let buf: string[] = [];
  const flush = () => {
    if (buf.length) {
      partes.push({ tipo: "texto", texto: buf.join("\n") });
      buf = [];
    }
  };
  let i = 0;
  while (i < linhas.length) {
    const cur = linhas[i];
    // próxima linha não-vazia
    let j = i + 1;
    while (j < linhas.length && linhas[j].trim() === "") j++;
    if (ehLinhaTabela(cur) && j < linhas.length && ehSeparadorTabela(linhas[j])) {
      flush();
      const bloco = [tirarCitacao(cur).trim()]; // cabeçalho (sem citação, B339)
      i = j + 1; // pula a linha separadora
      while (i < linhas.length) {
        if (ehLinhaTabela(linhas[i])) {
          bloco.push(tirarCitacao(linhas[i]).trim());
          i++;
          continue;
        }
        if (linhas[i].trim() === "") {
          // tolera linha(s) vazia(s) se ainda vier linha de tabela
          let m = i;
          while (m < linhas.length && linhas[m].trim() === "") m++;
          if (m < linhas.length && ehLinhaTabela(linhas[m])) {
            i = m;
            continue;
          }
        }
        break;
      }
      partes.push({ tipo: "tabela", linhas: bloco });
      continue;
    }
    buf.push(cur);
    i++;
  }
  flush();
  return partes;
}

const thTd: React.CSSProperties = {
  border: `1px solid ${theme.gray[300]}`,
  padding: "6px 10px",
  textAlign: "left",
  verticalAlign: "top",
  fontFamily: theme.fonts.sans,
  fontSize: "14px",
};


/**
 * 🧱 B374 (AI_156) — TABELA MARKDOWN COMO COMPONENTE REUSÁVEL.
 * Estava embutida aqui dentro, então a ANÁLISE GERAL — que tem renderizador
 * próprio (`formatarTextoAnalise`, em AbaAnaliseIA) — mostrava a tabela do
 * modelo como canos crus na tela. Extraída para fonte única, sem mudar o
 * desenho: os dois lugares passam a desenhar a MESMA tabela.
 */
export function TabelaMarkdown({
  linhas,
  chave = "t",
}: {
  linhas: string[];
  chave?: string;
}): JSX.Element {
  const header = parseLinhaTabela(linhas[0]);
  const dados = linhas
    .slice(1)
    .map(parseLinhaTabela)
    .filter((cels) => cels.some((c) => c.length > 0));
  return (
    <div style={{ overflowX: "auto", margin: "10px 0" }}>
      <table
        style={{
          borderCollapse: "collapse",
          width: "100%",
          fontFamily: theme.fonts.sans,
        }}
      >
        <thead>
          <tr>
            {header.map((c, ci) => (
              <th
                key={ci}
                style={{
                  ...thTd,
                  fontWeight: 600,
                  color: theme.gray[800],
                  background: theme.gray[50],
                }}
              >
                {renderInline(c, `th-${chave}-${ci}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dados.map((cels, ri) => (
            <tr key={ri}>
              {cels.map((c, ci) => (
                <td key={ci} style={{ ...thTd, color: theme.gray[700] }}>
                  {renderInline(c, `td-${chave}-${ri}-${ci}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function TextoAnaliseMarkdown({
  texto,
}: {
  texto: string;
}): JSX.Element {
  const baseText: React.CSSProperties = {
    fontSize: "15px",
    lineHeight: 1.6,
    color: theme.gray[700],
    fontFamily: theme.fonts.sans,
    margin: "0 0 8px 0",
  };

  const out: JSX.Element[] = [];
  let k = 0;

  const partes = separarPartes(removerBlocoTecnico(texto || ""));
  for (const parte of partes) {
    if (parte.tipo === "tabela") {
      out.push(<TabelaMarkdown key={k++} linhas={parte.linhas} chave={`t${k}`} />);
      continue;
    }

    renderTextoParte(parte.texto, out, baseText, () => k++);
  }

  return <div>{out}</div>;
}

// Renderiza uma PARTE de texto (não-tabela) reusando a lógica de linha original.
function renderTextoParte(
  texto: string,
  out: JSX.Element[],
  baseText: React.CSSProperties,
  nextKey: () => number
): void {
  const linhas = normalizar(texto).split(/\n/);

  for (const raw of linhas) {
    const linha = raw.trim();
    // pula linhas vazias ou só com marcadores (ex.: "##" solto sem título)
    // B339: ">" sozinho (citação vazia que o modelo emite entre blocos) aparecia
    // como um ">" literal no meio da análise.
    if (
      !linha ||
      /^#{1,6}$/.test(linha) ||
      linha === "-" ||
      linha === "•" ||
      linha === ">"
    )
      continue;

    if (linha === "---") {
      out.push(
        <hr
          key={nextKey()}
          style={{
            border: 0,
            borderTop: `1px solid ${theme.gray[300]}`,
            margin: "14px 0",
          }}
        />
      );
      continue;
    }

    // B339: `#{3,}` (o modelo usa #### com frequência) — antes /^###\s+/ não
    // casava "#### x" (o 4º "#" não é espaço) e o título saía LITERAL na tela.
    if (/^#{3,6}\s+/.test(linha)) {
      out.push(
        <h5
          key={nextKey()}
          style={{
            fontSize: "15px",
            fontWeight: 600,
            color: theme.gray[800],
            margin: "12px 0 4px",
            fontFamily: theme.fonts.sans,
          }}
        >
          {renderInline(linha.replace(/^#{3,}\s+/, ""), `h5`)}
        </h5>
      );
      continue;
    }

    if (/^##\s+/.test(linha)) {
      out.push(
        <h4
          key={nextKey()}
          style={{
            fontSize: "16px",
            fontWeight: 600,
            color: theme.gray[800],
            margin: "14px 0 6px",
            fontFamily: theme.fonts.sans,
          }}
        >
          {renderInline(linha.replace(/^#{2}\s+/, "").replace(/\*\*/g, ""), `h4`)}
        </h4>
      );
      continue;
    }

    if (/^#\s+/.test(linha)) {
      out.push(
        <h3
          key={nextKey()}
          style={{
            fontSize: "18px",
            fontWeight: 700,
            color: theme.primary[700],
            margin: "16px 0 8px",
            paddingBottom: "6px",
            borderBottom: `2px solid ${theme.primary[200]}`,
            fontFamily: theme.fonts.sans,
          }}
        >
          {renderInline(linha.replace(/^#\s+/, "").replace(/\*\*/g, ""), `h3`)}
        </h3>
      );
      continue;
    }

    // Blockquote → caixa de destaque colorida pelo MARCADOR real do conteúdo
    // (🔴 = crítico/vermelho; 🟡/⚠️ = atenção/amarelo; senão = citação cinza).
    if (/^>\s+/.test(linha)) {
      const conteudo = linha.replace(/^>\s+/, "");
      const cor = /🔴/.test(conteudo)
        ? { bg: "#fef2f2", bd: "#fecaca", fg: "#b91c1c" }
        : /⚠️|🟡/.test(conteudo)
        ? { bg: "#fffbeb", bd: "#fde68a", fg: "#b45309" }
        : null;
      out.push(
        cor ? (
          <div
            key={nextKey()}
            style={{
              background: cor.bg,
              border: `1px solid ${cor.bd}`,
              borderRadius: theme.borderRadius.md,
              padding: "10px 14px",
              margin: "8px 0",
              color: cor.fg,
              fontWeight: 500,
              fontFamily: theme.fonts.sans,
            }}
          >
            {renderInline(conteudo, `cb`)}
          </div>
        ) : (
          <blockquote
            key={nextKey()}
            style={{
              borderLeft: `3px solid ${theme.gray[300]}`,
              paddingLeft: 10,
              margin: "8px 0",
              color: theme.gray[700],
              fontStyle: "italic",
              fontFamily: theme.fonts.sans,
            }}
          >
            {renderInline(conteudo, `q`)}
          </blockquote>
        )
      );
      continue;
    }

    // Linha AVULSA começando com marcador de alerta real → caixa de destaque.
    // (Só no INÍCIO da linha — evita o falso-positivo do renderizador antigo, que
    // disparava por "ATENÇÃO"/palavra solta no meio do texto. Títulos ## ⚠️ já
    // foram tratados acima como cabeçalho.)
    if (/^(🔴|🟡|⚠️)/.test(linha)) {
      const vermelho = /^🔴/.test(linha);
      const cor = vermelho
        ? { bg: "#fef2f2", bd: "#fecaca", fg: "#b91c1c" }
        : { bg: "#fffbeb", bd: "#fde68a", fg: "#b45309" };
      out.push(
        <div
          key={nextKey()}
          style={{
            background: cor.bg,
            border: `1px solid ${cor.bd}`,
            borderRadius: theme.borderRadius.md,
            padding: "10px 14px",
            margin: "8px 0",
            color: cor.fg,
            fontWeight: 500,
            fontFamily: theme.fonts.sans,
          }}
        >
          {renderInline(linha, `box`)}
        </div>
      );
      continue;
    }

    if (/^[-•]\s+/.test(linha)) {
      out.push(
        <li
          key={nextKey()}
          style={{
            ...baseText,
            display: "list-item",
            listStyle: "disc",
            marginLeft: 20,
          }}
        >
          {renderInline(linha.replace(/^[-•]\s+/, ""), `li`)}
        </li>
      );
      continue;
    }

    out.push(
      <p key={nextKey()} style={baseText}>
        {renderInline(linha, `p`)}
      </p>
    );
  }
}
